/** @format */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography, withStyles } from '@material-ui/core';
import { configDefault } from '../../../core/config';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import Pager from '../../../components/Adescom/PagerRange';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import AddButton from '../../../components/Buttons/AddButton';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import { FilterReducerFieldName } from '../../../modules/filter/FilterReducers';
import orange from '@material-ui/core/colors/orange';
import GridItem from '../../../components/Grid/GridItem';
import I18n from '../../../components/Translate/I18n';
import GridContainer from '../../../components/Grid/GridContainer';
import { platformResellerConstants } from '../../../modules/platformreseller/actions';
import { Auth } from '../../../modules/auth/Auth';
import { platformResellerReducers } from '../../../modules/platformreseller/reducers';
import { store } from '../../../core/store';
import PlatformResellerInterface from '../../../interfaces/platform/PlatformResellerInterface';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';

interface BillingItemListProps {
    classes: any;
    selectPlatformReseller: (params: { mainname: string; id: number; nip: string }) => void;
    selectedPlatformUsers?: Array<{ mainname: string; id: number; nip: string }>;
}

store.injectReducer('platformResellerReducers', platformResellerReducers);

const BillingPlatformResellerList = (props: BillingItemListProps): React.ReactElement => {
    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([{ name: 'id', value: 'desc' }]);
    const URI = '/platform_resellers';

    const classes = props.classes;
    const dispatch = useDispatch();

    const { type, collections } = useSelector((state: { platformResellerReducers: any }) => {
        return state.platformResellerReducers;
    });

    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableRootNotMinWidth = { root: classes.tableRootNotMinWidth };

    const paginationCallbackFunction = (uri: string | null = null): void => {
        if (!uri) {
            uri = FormGeneralHelper.getPath(URI, configDefault.PLATFORM_API_PREFIX);
        }
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            platformResellerConstants.PLATFORM_RESELLER_LIST,
            filters,
            orders,
            (data: any) => {
                dispatch({
                    type: platformResellerConstants.PLATFORM_RESELLER_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);

    const clearFilter = (): void => {
        setOrders([{ name: 'id', value: 'desc' }]);
        setFilters([]);

        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    useEffect(() => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(URI, configDefault.PLATFORM_API_PREFIX)),
            platformResellerConstants.PLATFORM_RESELLER_LIST,
            [{name: 'itemsPerPage', value: 20}, {name: 'pagination', value: "true"}],
            [{name: 'id', value: 'desc'}],
            (data: any) => {
                dispatch({
                    type: platformResellerConstants.PLATFORM_RESELLER_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch]);

    return (
        <>
            <Table classes={tableRootNotMinWidth}>
                <TableHead>
                    <TableRow>
                        <TableCell classes={tableControlStickyHeader} colSpan={3} align="left" padding="none">
                            {(filters.length > 0 || orders.length > 0) && (
                                <CustomTextButtom
                                    size={'lg'}
                                    callback={clearFilter}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.clear"
                                />
                            )}
                            <CustomTextButtom size={'lg'} callback={paginationCallbackFunction} color="primary" label="general:common.search" />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell classes={tableControlStickyHeader} padding="none" align="left">
                            <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true}/>
                        </TableCell>
                        <TableCell classes={tableControlStickyHeader} align="left" padding="none">
                            <TableHeaderContentCell
                                textAlign="left"
                                title="simcard:list.partner"
                                fieldName="mainname"
                                showSort={true}
                                onlyFilter={false}
                                defaultDirection="desc"
                                searchFieldType="string"
                                updateFilterList={updateFilterList}
                                updateOrderList={updateOrderList}
                            />
                        </TableCell>
                        <TableCell classes={tableControlStickyHeader} align="left" padding="none">
                            <TableHeaderContentCell
                                textAlign="left"
                                title="platform_reseller:pr.Nip"
                                fieldName="nip"
                                showSort={true}
                                onlyFilter={false}
                                defaultDirection="desc"
                                searchFieldType="string"
                                updateFilterList={updateFilterList}
                                updateOrderList={updateOrderList}
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {collections &&
                        collections['hydra:member'].map((row: PlatformResellerInterface) => {
                            return (
                                <TableRow key={row.id} hover>
                                    <TableCell classes={tableCellRoot} align={'left'}>
                                        <AddButton
                                            callback={props.selectPlatformReseller}
                                            callbackParam={{ mainname: row.mainname, id: row.id, nip: row.nip }}
                                            disabled={props.selectedPlatformUsers && props.selectedPlatformUsers.length === 1}
                                        />
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} align={'left'}>
                                        {row.mainname}
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} align={'left'}>
                                        {row.nip}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                </TableBody>
                <TableFooter>
                    {collections && (
                        <TableRow>
                            <TableCell colSpan={11}>
                                <GridContainer style={{ marginLeft: '20px', marginBottom: '20px' }}>
                                    <GridItem xs={12} md={12} lg={12}>
                                        <GridContainer>
                                            <GridItem xs={12}>
                                                <Typography variant="subtitle2">
                                                    <I18n trans="prov:list.legend" />
                                                </Typography>
                                            </GridItem>
                                            <GridItem xs={1} style={{ backgroundColor: orange['A100'] }} />
                                            <GridItem xs={11}>
                                                <I18n trans="simcard:list.deleted" />
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </TableCell>
                        </TableRow>
                    )}
                </TableFooter>
                <TableFooter>
                    <TableCell classes={tableCellRoot} colSpan={3}>
                        {collections && collections['hydra:view'] && <Pager callback={paginationCallbackFunction} views={collections} />}
                    </TableCell>
                </TableFooter>
            </Table>
        </>
    );
};

export default withStyles(oplAppStyle)(BillingPlatformResellerList);
